"use client";

import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2 } from "lucide-react";
import { getBackendAssetUrl } from "@/lib/api-config";
import { PRODUCT_CATEGORIES, isOffer } from "@/lib/categories";
import ProductSection from "@/app/components/ProductSection";
import FloatingCart from "@/app/components/FloatingCart";
import { useCartContext } from "@/components/CartProvider";
import { useStockEngine } from "@/hooks/useStockEngine";
import CartSidebar from "@/components/landing/CartSidebar";
import { useAppAuth } from "@/hooks/useAppAuth";
import LoginModal from "@/app/components/LoginModal";
import MobileNumberModal from "@/app/components/MobileNumberModal";
import QrCodeModal from "@/app/components/QrCodeModal";
import PaymentModal from "@/components/landing/PaymentModal";
import { OfferCountdownBanner } from "@/app/components/NewUiLanding";
import RegionMismatchModal from "@/components/RegionMismatchModal";

const storefrontProductImage = (url?: string | null) => {
  if (!url) return "";
  if (
    url.startsWith("/images/") ||
    url.startsWith("/assets/")
  ) {
    return url;
  }
  return getBackendAssetUrl(url);
};

export default function GamePurchaseSection({
  game,
  region,
  compact = false,
  theme = "baasil",
}: {
  game: any;
  region: any;
  compact?: boolean;
  theme?: "baasil" | "linzzo";
}) {
  const linzzo = theme === "linzzo";
  const auth = useAppAuth();
  const {
    cart,
    setCart,
    clearCart,
    removeFromCart,
    updateQty,
    uid,
    setUid,
    validationResult,
    setValidationResult,
    mergeProducts,
    products,
    stockAvailability,
    regionPools,
    charges,
    paymentConfig,
    activeOffers,
    hydrateCatalog,
  } = useCartContext();
  const [playerId, setPlayerId] = useState("");
  const [zoneId, setZoneId] = useState("");
  const [player, setPlayer] = useState<any>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isMobileModalOpen, setIsMobileModalOpen] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    string | null
  >(null);
  const [isSubmittingOrder, setIsSubmittingOrder] = useState(false);
  const [pendingGenieOrder, setPendingGenieOrder] = useState<any>(null);
  const [pendingCeypayOrder, setPendingCeypayOrder] = useState<any>(null);
  const [pubgProductType, setPubgProductType] = useState("All");
  const [showIdMismatchModal, setShowIdMismatchModal] = useState(false);
  const [pendingAutoValidate, setPendingAutoValidate] = useState<string | null>(
    null,
  );
  const [mismatchData, setMismatchData] = useState<{
    detectedRegionCode: string;
    detectedRegionName: string;
    currentRegionCode: string;
    currentRegionName: string;
    targetUrl: string;
    actionButtonText: string;
  } | null>(null);
  const [gameOffers, setGameOffers] = useState<any[]>([]);
  const apiUrl = process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000/api";
  const verificationScope = `${game.slug}:${region?.slug || region?.code || "global"}`;
  const verificationStorageKey = `topupbot_catalog_verification:${verificationScope}`;

  useEffect(() => {
    let saved: any = null;
    try {
      const raw = window.localStorage.getItem(verificationStorageKey);
      saved = raw ? JSON.parse(raw) : null;
    } catch {
      saved = null;
    }

    if (saved?.valid && saved.catalogScope === verificationScope) {
      const savedPlayerId = String(
        saved.playerId || saved.uid || saved.userId || "",
      );
      setPlayerId(savedPlayerId);
      setZoneId(String(saved.zoneId || saved.zone_id || ""));
      setPlayer(saved);
      setUid(savedPlayerId);
      setValidationResult(saved);
      return;
    }

    try {
      const prefillUid = window.sessionStorage.getItem("topupbot_prefill_uid");
      if (prefillUid) {
        setPlayerId(prefillUid);
        setUid(prefillUid);
        setError("");
        setPlayer(null);
        window.sessionStorage.removeItem("topupbot_prefill_uid");
        setPendingAutoValidate(prefillUid);
        return;
      }
    } catch {}

    setPlayer(null);
    setPlayerId("");
    setZoneId("");
    setError("");
    setUid("");
    setValidationResult(null);
  }, [verificationScope, verificationStorageKey, setUid, setValidationResult]);

  const isFf =
    game.slug === "free-fire" ||
    /free\s*fire|freefire/i.test(game.name || "");
  const activeRegionCode = (
    region?.code ||
    ""
  ).toUpperCase();
  const activeRegionSlug = (
    region?.slug ||
    region?.code ||
    ""
  ).toLowerCase();
  const isIdRegion =
    isFf &&
    (activeRegionCode === "ID" ||
      activeRegionSlug === "indonesia" ||
      activeRegionSlug === "id");

  const catalogProducts = useMemo(
    () =>
      (game.products || [])
        .filter((product: any) => {
          if (isIdRegion) {
            return (
              !product.gamerhubProductCode &&
              product.defaultRegion !== "GAMERHUB" &&
              !product.id?.toLowerCase().startsWith("gh-")
            );
          }
          return true;
        })
        .map((product: any) => ({
          ...product,
          imageUrl: product.imageUrl || game.imageUrl,
          game: product.game || game.name,
          defaultRegion: product.defaultRegion || "GAMERHUB",
        })),
    [game.products, game.imageUrl, game.name, isIdRegion],
  );

  useEffect(() => {
    mergeProducts(catalogProducts);
  }, [catalogProducts, mergeProducts]);

  const currentProductIds = useMemo(
    () => new Set(catalogProducts.map((product: any) => product.id)),
    [catalogProducts],
  );

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${apiUrl}/public/active-offers`)
      .then((response) => {
        if (cancelled) return;
        const offers = response.data?.data || response.data || [];
        const relevantOffers = Array.isArray(offers)
          ? offers.filter((offer: any) =>
              offer.products?.some((entry: any) =>
                currentProductIds.has(entry.productId),
              ),
            )
          : [];
        setGameOffers(relevantOffers);

        const offerMap: Record<string, any> = {};
        for (const offer of Array.isArray(offers) ? offers : []) {
          if (Array.isArray(offer.products)) {
            for (const op of offer.products) {
              offerMap[op.productId] = {
                ...op,
                offerName: offer.name,
                endTime: offer.endTime,
              };
            }
          }
        }
        if (
          Object.keys(offerMap).length > 0 &&
          typeof hydrateCatalog === "function"
        ) {
          hydrateCatalog({ activeOffers: offerMap });
        }
      })
      .catch(() => {
        if (!cancelled) setGameOffers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [apiUrl, currentProductIds, hydrateCatalog]);

  const effectiveOffers = useMemo(() => {
    const map: Record<string, any> = { ...activeOffers };
    for (const offer of gameOffers) {
      if (Array.isArray(offer.products)) {
        for (const op of offer.products) {
          map[op.productId] = {
            ...op,
            offerName: offer.name,
            endTime: offer.endTime,
          };
        }
      }
    }
    return map;
  }, [activeOffers, gameOffers]);

  useEffect(() => {
    if (stockAvailability && Object.keys(stockAvailability).length > 0) return;
    let isMounted = true;
    axios
      .get("/api/public/stock-availability")
      .then((res) => {
        if (!isMounted) return;
        const stockData = res.data?.data || res.data || {};
        if (stockData.products) {
          const map: Record<string, any> = {};
          for (const p of stockData.products) {
            map[p.productId] = {
              isAvailable: p.isAvailable,
              shellsNeeded: p.shellsNeeded,
              shellsSg: p.shellsSg ?? 0,
              shellsMy: p.shellsMy ?? 0,
              shellsId: p.shellsId ?? 0,
              maxShellsInSingleAccount: p.maxShellsInSingleAccount,
              totalAvailableShells: p.totalAvailableShells,
              defaultRegion: p.defaultRegion,
              maxQty: p.maxQty ?? (p.isAvailable ? 99 : 0),
              isBundle: p.isBundle ?? false,
              constituents: p.constituents ?? undefined,
            };
          }
          const pools: Record<string, any> = {};
          if (stockData.summary?.regions) {
            for (const [reg, info] of Object.entries(
              stockData.summary.regions as Record<string, any>,
            )) {
              pools[reg] = {
                accounts: info.accounts ?? 0,
                totalShells: info.totalShells ?? 0,
                maxAccountShells: info.maxAccountShells ?? 0,
                accountShells: info.accountShells ?? [],
                publicReserved: info.publicReserved ?? 0,
              };
            }
          }
          hydrateCatalog({ stockAvailability: map, regionPools: pools });
        }
      })
      .catch((e) => console.error("Failed to fetch game stock availability:", e));
    return () => {
      isMounted = false;
    };
  }, [stockAvailability, hydrateCatalog]);

  const scopedCart = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(cart).filter(([id]) => currentProductIds.has(id)),
      ),
    [cart, currentProductIds],
  );

  const scopedSubtotal = useMemo(
    () =>
      Object.entries(scopedCart).reduce((total, [id, item]) => {
        const product = catalogProducts.find((entry: any) => entry.id === id);
        const price = Number(
          effectiveOffers?.[id]?.discountedPrice ??
            product?.websitePriceLkr ??
            product?.priceLkr ??
            0,
        );
        return total + price * item.qty;
      }, 0),
    [scopedCart, catalogProducts, effectiveOffers],
  );

  const scopedTotal = charges?.processingChargeEnabled
    ? scopedSubtotal * (1 + Number(charges.processingChargePercent || 0) / 100)
    : scopedSubtotal;

  const playerRegion =
    validationResult?.region === "ID"
      ? "ID"
      : isIdRegion
        ? "ID"
        : "SG";

  const stockEngine = useStockEngine(
    scopedCart,
    stockAvailability,
    regionPools,
    playerRegion,
  );

  const outOfStockEntries = useMemo(
    () =>
      Object.entries(scopedCart).filter(([itemId]) => {
        const info = stockAvailability?.[itemId];
        const feas = stockEngine.feasibility?.[itemId];
        return (info && !info.isAvailable) || feas?.status === "out_of_stock";
      }),
    [scopedCart, stockAvailability, stockEngine.feasibility],
  );

  const hasOutOfStockItems = outOfStockEntries.length > 0;

  const outOfStockNames = useMemo(
    () =>
      outOfStockEntries.map(([itemId]) => {
        const prod = catalogProducts.find((p: any) => p.id === itemId);
        return prod?.name || itemId;
      }),
    [outOfStockEntries, catalogProducts],
  );

  const addGameProduct = (productId: string, item: any) => {
    const stockInfo = stockAvailability?.[productId];
    const feas = stockEngine.feasibility?.[productId];
    if (
      (stockInfo && !stockInfo.isAvailable) ||
      feas?.status === "out_of_stock" ||
      (feas && !feas.canAdd && (stockEngine.effectiveMaxQty?.[productId] ?? 0) <= 0)
    ) {
      setError(`Product "${item.name || productId}" is currently out of stock.`);
      return;
    }
    setCart((previous) => {
      const sameGameItems = Object.fromEntries(
        Object.entries(previous).filter(([id]) => currentProductIds.has(id)),
      );
      return {
        ...sameGameItems,
        [productId]: {
          ...item,
          region: region?.code || "GLOBAL",
        },
      };
    });
  };

  const buildOrderDetails = () => ({
    playerId:
      validationResult?.userId ||
      validationResult?.uid ||
      validationResult?.playerId ||
      uid ||
      playerId,
    region: validationResult?.region || region?.code || "GLOBAL",
    playerName: validationResult?.nickname || player?.nickname || "",
    ...(zoneId || validationResult?.zoneId || validationResult?.zone_id
      ? {
          zoneId:
            zoneId || validationResult?.zoneId || validationResult?.zone_id,
        }
      : {}),
    items: Object.entries(scopedCart).map(([id, item]) => ({
      itemId: id,
      quantity: item.qty,
    })),
    ...(auth.email ? { userEmail: auth.email } : {}),
    ...(auth.firebaseUser
      ? { firebaseUid: auth.firebaseUser.uid }
      : auth.oidcAuth?.user?.profile?.sub
        ? { firebaseUid: auth.oidcAuth.user.profile.sub }
        : {}),
  });

  const handleCheckout = () => {
    if (hasOutOfStockItems) {
      setError(
        `Cannot checkout: Out of stock item(s): ${outOfStockNames.join(", ")}`,
      );
      return;
    }
    if (hasZoneInput && !zoneId.trim()) {
      setError(`Please enter your ${zoneLabel}`);
      return;
    }
    if (!auth.isAuthenticated) {
      setIsLoginModalOpen(true);
      return;
    }
    if (auth.hasMobile === false) {
      setIsMobileModalOpen(true);
      return;
    }
    setSelectedPaymentMethod(null);
    setShowPaymentModal(true);
  };

  const handleGenieCheckout = async (mode: "CARD" | "QR" = "CARD") => {
    setIsSubmittingOrder(true);
    try {
      const response = await axios.post("/api/payment/public-order", {
        gateway: "GENIE",
        orderDetails: {
          ...buildOrderDetails(),
          paymentMethod: mode === "QR" ? "QR" : undefined,
        },
      });
      const data = response.data.data || response.data;
      if (mode === "CARD" && data.paymentUrl) {
        window.location.href = data.paymentUrl;
      } else if (data.qrCode || data.paymentUrl) {
        setPendingGenieOrder(data);
        setShowPaymentModal(false);
      }
    } catch (checkoutError: any) {
      alert(
        checkoutError.response?.data?.message || "Failed to initialize payment",
      );
    } finally {
      setIsSubmittingOrder(false);
    }
  };

  const handleBcoinCheckout = async () => {
    setIsSubmittingOrder(true);
    try {
      const token = await auth.getToken();
      const response = await axios.post(
        "/api/payment/public-order-with-bcoins",
        { orderDetails: buildOrderDetails() },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      const data = response.data.data || response.data;
      const statusId =
        data?.id || data?.transactionId || data?.batchId || data?.orderBatchId;
      if (!statusId) throw new Error("Payment status ID was not returned");
      clearCart();
      setShowPaymentModal(false);
      window.location.href = `/payment-status?id=${encodeURIComponent(String(statusId))}`;
    } catch (checkoutError: any) {
      alert(
        checkoutError.response?.data?.message ||
          checkoutError.message ||
          "Failed to initialize BCoins payment",
      );
    } finally {
      setIsSubmittingOrder(false);
    }
  };

  const handleEzCashCheckout = async (referenceNumber: string) => {
    const token = await auth.getToken();
    const response = await axios.post(
      "/api/payment/public-order-with-ezcash",
      { orderDetails: buildOrderDetails(), referenceNumber },
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );
    return response.data.data || response.data;
  };

  const handleCeypayCheckout = async (
    provider: "BINANCE" | "KUCOIN" | "BYBIT",
  ) => {
    setIsSubmittingOrder(true);
    try {
      const response = await axios.post("/api/payment/public-order", {
        gateway: "CEYPAY",
        orderDetails: {
          ...buildOrderDetails(),
          paymentMethod: provider,
        },
      });
      const data = response.data.data || response.data;
      if (data.qrCode) {
        setPendingCeypayOrder(data);
        setShowPaymentModal(false);
      } else if (data.paymentUrl) {
        window.location.href = data.paymentUrl;
      } else if (data.id) {
        window.location.href = `/payment-status?id=${encodeURIComponent(String(data.id))}`;
      } else {
        throw new Error("Payment link was not returned");
      }
    } catch (checkoutError: any) {
      alert(
        checkoutError.response?.data?.message ||
          checkoutError.message ||
          "Failed to initialize crypto payment",
      );
    } finally {
      setIsSubmittingOrder(false);
    }
  };

  const inputForm =
    Array.isArray(region?.inputForm) && region.inputForm.length > 0
      ? region.inputForm
      : Array.isArray(game?.inputForm) && game.inputForm.length > 0
        ? game.inputForm
        : [];
  const hasZoneInput =
    inputForm.length > 1 ||
    inputForm.some((f: any) =>
      /zone|server|input2/i.test(f.field || f.name || f.key || f.label || ""),
    ) ||
    /mobile-legends|mlbb|genshin/i.test(game?.slug || "") ||
    /mobile legends|mlbb/i.test(game?.name || "");

  const playerLabel =
    inputForm[0]?.label ||
    (/genshin/i.test(game?.slug || "")
      ? "UID"
      : `${game?.name || "Player"} ID`);
  const zoneLabel =
    inputForm[1]?.label ||
    (/genshin/i.test(game?.slug || "") ? "Server" : "Zone ID");

  const categorizedProducts = (() => {
    const products = catalogProducts;
    if (game.slug === "pubg-mobile") {
      const groups = [
        {
          label: "UC",
          match: (product: any) => /\buc\b/i.test(product.name || ""),
        },
        {
          label: "WOW Coins",
          match: (product: any) => /wow\s*coins?/i.test(product.name || ""),
        },
        {
          label: "Other",
          match: () => true,
        },
      ];
      const assigned = new Set<string>();
      const result: any[] = [];

      for (const group of groups) {
        const matches = products.filter(
          (product: any) => !assigned.has(product.id) && group.match(product),
        );
        matches.forEach((product: any, index: number) => {
          assigned.add(product.id);
          result.push({
            ...product,
            _groupLabel: group.label,
            _showGroupHeading: index === 0,
          });
        });
      }
      return result;
    }

    if (game.slug !== "free-fire") return products;

    const categoryOrder = [
      "offers",
      "subscriptions",
      "level_ups",
      "evos",
      "diamonds",
    ];
    const assigned = new Set<string>();
    const result: any[] = [];

    for (const key of categoryOrder) {
      const category = PRODUCT_CATEGORIES.find((item) => item.key === key);
      if (!category) continue;
      const matches = products.filter(
        (product: any) => !assigned.has(product.id) && category.match(product),
      );
      matches.forEach((product: any, index: number) => {
        assigned.add(product.id);
        result.push({
          ...product,
          _groupLabel: category.label,
          _showGroupHeading: index === 0,
        });
      });
    }

    const other = products.filter((product: any) => !assigned.has(product.id) && !isOffer(product));
    other.forEach((product: any, index: number) =>
      result.push({
        ...product,
        _groupLabel: "Other Packages",
        _showGroupHeading: index === 0,
      }),
    );
    return result;
  })();

  const productGroups = categorizedProducts.reduce(
    (groups: Array<{ label: string; products: any[] }>, product: any) => {
      const label = product._groupLabel || "";
      const current = groups[groups.length - 1];
      if (!current || current.label !== label) {
        groups.push({ label, products: [product] });
      } else {
        current.products.push(product);
      }
      return groups;
    },
    [],
  );
  const displayedProductGroups =
    game.slug === "pubg-mobile" && pubgProductType !== "All"
      ? productGroups.filter((group) => group.label === pubgProductType)
      : productGroups;

  const validate = async (targetPlayerId?: string) => {
    const rawId =
      typeof targetPlayerId === "string" ? targetPlayerId : playerId;
    const trimmedId = (rawId || "").trim();
    if (!trimmedId) {
      setError(`Please enter your ${playerLabel}`);
      return;
    }
    if (hasZoneInput && !zoneId.trim()) {
      setError(`Please enter your ${zoneLabel}`);
      return;
    }
    setLoading(true);
    setError("");
    setPlayer(null);
    try {
      const response = await fetch(`${apiUrl}/public/validate-player`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          playerId: trimmedId,
          zoneId: hasZoneInput ? zoneId.trim() : undefined,
          region: region?.code || "SG",
          game: game.slug,
          productCode:
            game.slug === "free-fire"
              ? undefined
              : region?.providerRegion || undefined,
        }),
      });
      const json = await response.json();
      const envelope = json?.data || json || {};
      const nestedPlayer =
        envelope?.data &&
        typeof envelope.data === "object" &&
        !Array.isArray(envelope.data)
          ? envelope.data
          : {};
      const result = { ...envelope, ...nestedPlayer };
      const isMismatch =
        result?.error === "REGION_MISMATCH" ||
        result?.reason === "region_mismatch" ||
        json?.error === "REGION_MISMATCH";

      if (isMismatch) {
        let detectedRegion = (
          result?.playerRegion ||
          result?.region ||
          json?.playerRegion ||
          json?.region ||
          ""
        ).toUpperCase();
        if (
          detectedRegion === "LK" ||
          detectedRegion === "SRI LANKA" ||
          detectedRegion === "SRILANKA" ||
          detectedRegion.includes("LANKA")
        ) {
          detectedRegion = "SG";
        }
        const currentRegionCode = (region?.code || "SG").toUpperCase();

        const isSameCluster = (r1: string, r2: string) => {
          let c1 = (r1 || "").toUpperCase();
          let c2 = (r2 || "").toUpperCase();
          if (c1 === "LK" || c1 === "SRI LANKA" || c1 === "SRILANKA" || c1.includes("LANKA")) c1 = "SG";
          if (c2 === "LK" || c2 === "SRI LANKA" || c2 === "SRILANKA" || c2.includes("LANKA")) c2 = "SG";
          if (c1 === c2) return true;
          const sgCluster = [
            "SG",
            "MY",
            "LK",
            "SRI LANKA",
            "SRILANKA",
            "MY-SG",
            "MY/SG",
            "SG/MY",
            "SG-MY",
            "KH/SG/MY",
          ];
          return sgCluster.includes(c1) && sgCluster.includes(c2);
        };

        if (
          detectedRegion &&
          !isSameCluster(detectedRegion, currentRegionCode)
        ) {
          const regionSlugMap: Record<string, string> = {
            ID: "indonesia",
            SG: "my-sg",
            MY: "my-sg",
            LK: "my-sg",
            TW: "tw",
            VN: "vn",
            TH: "th",
            PH: "ph",
            LATAM: "latam",
            BR: "br",
            GLOBAL: "global",
            BD: "bd",
          };
          const regionNames: Record<string, string> = {
            ID: "Indonesia (ID)",
            SG: "Singapore / Malaysia (SG/MY)",
            MY: "Singapore / Malaysia (SG/MY)",
            LK: "Singapore / Malaysia (SG/MY)",
            TW: "Taiwan (TW)",
            VN: "Vietnam (VN)",
            TH: "Thailand (TH)",
            PH: "Philippines (PH)",
            LATAM: "Latin America (LATAM)",
            BR: "Brazil (BR)",
            GLOBAL: "Global",
            BD: "Bangladesh (BD)",
          };
          const detectedSlug =
            regionSlugMap[detectedRegion] || detectedRegion.toLowerCase();
          const targetRegionObj = (game.regions || []).find(
            (r: any) =>
              r.code?.toUpperCase() === detectedRegion ||
              r.slug?.toLowerCase() === detectedSlug ||
              r.slug?.toLowerCase() === detectedRegion.toLowerCase(),
          );
          const detectedName =
            targetRegionObj?.name ||
            regionNames[detectedRegion] ||
            detectedRegion;
          const currentName =
            region?.name ||
            regionNames[currentRegionCode] ||
            currentRegionCode;
          const targetUrl =
            game.slug === "free-fire"
              ? `/games/free-fire?region=${targetRegionObj?.slug || detectedSlug}`
              : targetRegionObj?.slug
                ? `/games/${game.slug}?region=${targetRegionObj.slug}`
                : `/games/${game.slug}?region=${detectedSlug}`;

          setMismatchData({
            detectedRegionCode: detectedRegion,
            detectedRegionName: detectedName,
            currentRegionCode: currentRegionCode,
            currentRegionName: currentName,
            targetUrl,
            actionButtonText: `Switch to ${game.name} ${detectedName} Top Up`,
          });
          setShowIdMismatchModal(true);
        }
      }
      if (!response.ok || (!result.valid && !result.nickname)) {
        throw new Error(
          (typeof result.message === "string" && result.message) ||
            (typeof json.message === "string" && json.message) ||
            "Player ID was not found",
        );
      }
      const scopedResult = {
        ...result,
        valid: true,
        uid: result.uid || result.playerId || trimmedId,
        playerId: result.playerId || trimmedId,
        zoneId: hasZoneInput ? zoneId.trim() : undefined,
        region: region?.code || result.region || "GLOBAL",
        gameSlug: game.slug,
        regionSlug: region?.slug || null,
        catalogScope: verificationScope,
      };
      setPlayer(scopedResult);
      setPlayerId(trimmedId);
      setUid(trimmedId);
      setValidationResult(scopedResult);
      window.localStorage.setItem(
        verificationStorageKey,
        JSON.stringify(scopedResult),
      );
    } catch (err: any) {
      setError(err.message || "Unable to validate Player ID");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (pendingAutoValidate) {
      validate(pendingAutoValidate);
      setPendingAutoValidate(null);
    }
  }, [pendingAutoValidate]);

  return (
    <section
      aria-labelledby="packages-heading"
      className={linzzo ? "mx-auto max-w-6xl px-4 py-10 md:py-12" : undefined}
    >
      <div className="text-center">
        <p
          className={`text-xs font-bold uppercase tracking-[0.22em] ${linzzo ? "text-amber-300" : "text-[#548DC8]"}`}
        >
          Available packages
        </p>
        <h2
          id="packages-heading"
          className={`mt-2 font-display font-normal uppercase leading-none ${linzzo ? "text-white" : "text-[#343434]"} ${compact ? "text-[34px] md:text-[48px]" : "text-[40px] md:text-[64px]"}`}
        >
          Choose your{" "}
          <span className={linzzo ? "text-amber-300" : "text-[#5A98D8]"}>
            top-up
          </span>
        </h2>
        <p className="mt-3 text-xs font-medium uppercase tracking-wide text-[#A8A8A8] md:text-sm">
          Verify the Player ID {hasZoneInput ? "and Zone ID " : ""}for this game
          and region before selecting a package.
        </p>
      </div>
      <div
        className={`${compact ? "mt-5" : "mt-8"} flex flex-col items-center justify-center px-4`}
      >
        {player ? (
          <div className="animate-in zoom-in flex w-full max-w-sm items-center justify-between rounded-md border border-emerald-200 bg-emerald-50 px-5 py-3 shadow-sm duration-300">
            <div className="flex min-w-0 items-center gap-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full border border-emerald-100 bg-white shadow-sm">
                {game.imageUrl ? (
                  <img
                    src={getBackendAssetUrl(game.imageUrl)}
                    alt=""
                    className="h-8 w-8 rounded-full object-contain"
                  />
                ) : (
                  <CheckCircle2 className="h-6 w-6 text-emerald-500" />
                )}
              </div>
              <div className="min-w-0 text-left">
                <p className="truncate font-semibold leading-tight text-emerald-900">
                  {player.nickname || "Player verified"}
                </p>
                <p className="truncate text-[11px] font-medium tracking-wide text-emerald-600">
                  {playerLabel}: {playerId}
                  {zoneId ? ` · ${zoneLabel}: ${zoneId}` : ""}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                window.localStorage.removeItem(verificationStorageKey);
                setPlayer(null);
                setPlayerId("");
                setZoneId("");
                setUid("");
                setValidationResult(null);
              }}
              className="ml-4 shrink-0 text-[10px] font-bold uppercase tracking-widest text-emerald-600 transition-colors hover:text-emerald-800"
            >
              Change
            </button>
          </div>
        ) : (
          <div className="flex w-full max-w-2xl flex-col items-center justify-center sm:flex-row">
            <div className="relative w-full max-w-xs sm:w-72">
              <input
                type="text"
                value={playerId}
                onChange={(event) => {
                  setPlayerId(event.target.value);
                  setError("");
                }}
                onKeyDown={(event) => event.key === "Enter" && validate()}
                placeholder={`Enter your ${playerLabel}`}
                autoComplete="off"
                className="w-full rounded-sm border border-border bg-card px-5 py-3 pr-10 text-sm font-normal uppercase tracking-wider text-foreground outline-none placeholder:text-muted-foreground focus:ring-2 focus:ring-ring sm:rounded-r-none"
              />
            </div>
            {hasZoneInput && (
              <div className="relative mt-2 w-full max-w-xs sm:mt-0 sm:w-44">
                <input
                  type="text"
                  value={zoneId}
                  onChange={(event) => {
                    setZoneId(event.target.value);
                    setError("");
                  }}
                  onKeyDown={(event) => event.key === "Enter" && validate()}
                  placeholder={`Enter your ${zoneLabel}`}
                  autoComplete="off"
                  className="w-full rounded-sm border border-border bg-card px-5 py-3 text-sm font-normal uppercase tracking-wider text-foreground outline-none placeholder:text-muted-foreground focus:ring-2 focus:ring-ring sm:rounded-none"
                />
              </div>
            )}
            <button
              type="button"
              onClick={() => validate()}
              disabled={loading || !playerId || (hasZoneInput && !zoneId)}
              className={`mt-2 inline-flex w-full max-w-xs items-center justify-center rounded-sm px-6 py-3 text-sm font-bold shadow-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 sm:mt-0 sm:w-auto sm:rounded-l-none ${linzzo ? "bg-amber-300 text-black hover:bg-amber-200" : "bg-[#5A98D8] text-white hover:bg-[#337fc9]"}`}
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {loading ? "VERIFYING..." : "VERIFY PLAYER"}
            </button>
          </div>
        )}
        {error && !player && (
          <p
            role="alert"
            className="animate-in fade-in mt-3 text-center text-xs font-semibold text-red-500 duration-200"
          >
            {error}
          </p>
        )}
      </div>
      {gameOffers.length > 0 && (
        <div className="mt-8 space-y-4">
          {gameOffers.map((offer) => (
            <OfferCountdownBanner key={offer.id} offer={offer} />
          ))}
        </div>
      )}
      {game.products?.length ? (
        <div className={compact ? "mt-5" : "mt-8"}>
          {game.slug === "pubg-mobile" && (
            <div
              className="mb-7 flex flex-wrap justify-center gap-2"
              role="tablist"
              aria-label="PUBG Mobile product type"
            >
              {["All", "UC", "WOW Coins", "Other"].map((type) => (
                <button
                  key={type}
                  type="button"
                  role="tab"
                  aria-selected={pubgProductType === type}
                  onClick={() => setPubgProductType(type)}
                  className={`min-w-24 rounded-md border px-4 py-2.5 text-sm font-bold transition ${
                    pubgProductType === type
                      ? "border-[#5A98D8] bg-[#5A98D8] text-white shadow-sm"
                      : "border-blue-100 bg-white text-slate-600 hover:border-[#8DBBEA] hover:text-[#337fc9]"
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          )}
          {displayedProductGroups.map((group) => (
            <ProductSection
              key={group.label || "packages"}
              title={group.label || "Available Packages"}
              products={group.products.map((product: any) => ({
                ...product,
                _offer: effectiveOffers?.[product.id] || null,
                price: `Rs.${Number(
                  effectiveOffers?.[product.id]?.discountedPrice ??
                    product.websitePriceLkr ??
                    product.priceLkr ??
                    0,
                ).toFixed(2)}`,
                image: storefrontProductImage(
                  product.imageUrl || game.imageUrl,
                ),
              }))}
              cart={scopedCart}
              stockAvailability={stockAvailability}
              feasibility={stockEngine.feasibility}
              effectiveMaxQty={stockEngine.effectiveMaxQty}
              onAdd={addGameProduct}
              onRemove={removeFromCart}
              onUpdateQty={updateQty}
              backgroundColor={linzzo ? "#18181B" : "#F2F8FF"}
              textColor={linzzo ? "#FACC15" : "#1F4F82"}
              accentColor={linzzo ? "#FACC15" : "#5998D8"}
              onNoPlayer={
                player
                  ? undefined
                  : () =>
                      setError(
                        hasZoneInput
                          ? `Please enter and verify your ${playerLabel} and ${zoneLabel} to continue`
                          : `Please enter and verify your ${playerLabel} to continue`,
                      )
              }
              isSpecialOffer={group.label === "One Time Offer"}
            />
          ))}
        </div>
      ) : (
        <div className="mt-6 rounded-2xl border border-dashed border-border p-10 text-center text-muted-foreground">
          Select a region to view available packages.
        </div>
      )}
      <FloatingCart
        itemCount={Object.values(scopedCart).reduce(
          (total, item) => total + (item.qty || 0),
          0,
        )}
        uid={uid}
        validationResult={validationResult}
      >
        <CartSidebar
          cart={scopedCart}
          products={products}
          channelLevelUps={[]}
          stockAvailability={stockAvailability}
          feasibility={stockEngine.feasibility}
          subtotal={scopedSubtotal}
          referralDiscount={0}
          total={scopedTotal}
          referralBalance={0}
          useReferralBalance={false}
          setUseReferralBalance={() => undefined}
          isAuthenticated={auth.isAuthenticated}
          hasMobile={auth.hasMobile}
          validationResult={validationResult}
          isSubmittingOrder={isSubmittingOrder}
          hasOutOfStockItems={hasOutOfStockItems}
          outOfStockNames={outOfStockNames}
          onClear={clearCart}
          onUpdateQty={updateQty}
          onRemove={removeFromCart}
          onCheckout={handleCheckout}
          isMobile
          paymentConfig={paymentConfig}
          charges={charges}
          activeOffers={activeOffers}
        />
      </FloatingCart>
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onLoginSuccess={() => {
          auth.refreshSession();
          setIsLoginModalOpen(false);
        }}
      />
      <MobileNumberModal
        isOpen={isMobileModalOpen}
        onClose={() => setIsMobileModalOpen(false)}
      />
      {showPaymentModal && (
        <PaymentModal
          show={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          baseAmount={scopedSubtotal}
          charges={charges}
          selectedPaymentMethod={selectedPaymentMethod}
          setSelectedPaymentMethod={setSelectedPaymentMethod}
          isSubmittingOrder={isSubmittingOrder}
          onGenieCheckout={handleGenieCheckout}
          onCeypayCheckout={handleCeypayCheckout}
          onBcoinCheckout={handleBcoinCheckout}
          onEzCashCheckout={handleEzCashCheckout}
          paymentConfig={paymentConfig}
        />
      )}
      {pendingGenieOrder && (
        <QrCodeModal
          isOpen={!!pendingGenieOrder}
          onClose={() => setPendingGenieOrder(null)}
          orderData={pendingGenieOrder}
        />
      )}
      {pendingCeypayOrder && (
        <QrCodeModal
          isOpen={!!pendingCeypayOrder}
          onClose={() => setPendingCeypayOrder(null)}
          orderData={pendingCeypayOrder}
        />
      )}
      <RegionMismatchModal
        isOpen={showIdMismatchModal}
        onClose={() => setShowIdMismatchModal(false)}
        gameName={game.name || "Game"}
        gameSlug={game.slug}
        playerId={playerId}
        detectedRegionCode={mismatchData?.detectedRegionCode || "ID"}
        detectedRegionName={
          mismatchData?.detectedRegionName || "Indonesia (ID)"
        }
        currentRegionCode={
          mismatchData?.currentRegionCode ||
          (region?.code ? region.code.toUpperCase() : "SG")
        }
        currentRegionName={
          mismatchData?.currentRegionName ||
          region?.name ||
          region?.code ||
          "Singapore / Malaysia (SG/MY)"
        }
        targetUrl={mismatchData?.targetUrl || `/games/${game.slug}`}
        actionButtonText={mismatchData?.actionButtonText}
      />
    </section>
  );
}
